import { useEffect, useRef, useState } from 'react';

const features = [
  {
    number: '01',
    kicker: 'Curadoria',
    title: 'Compramos como se fosse pra casa',
    desc: 'Nada de matéria-prima esquecida em estoque. A cada manhã escolhemos, na mão, o que vai virar o sanduíche do seu dia — e o que não passa no teste, fica de fora.',
    image:
      'https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20fresh%20vibrant%20vegetables%20and%20herbs%20scattered%20on%20a%20dark%20slate%20surface%2C%20warm%20dramatic%20side%20light%2C%20premium%20editorial%20food%20photography%2C%20moody%20warm%20tonal%20palette%2C%20high%20detail%20natural%20textures&width=800&height=1000&seq=nat-dif-curadoria-01&orientation=portrait',
  },
  {
    number: '02',
    kicker: 'Embalagem',
    title: 'Frescor trancado a vácuo',
    desc: 'Cada unidade sai selada individualmente, longe do ar e da umidade. O sabor chega como saiu da cozinha — sem conservantes artificiais fazendo o trabalho.',
    image:
      'https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20vacuum%20sealed%20transparent%20food%20package%20resting%20on%20a%20dark%20rustic%20wooden%20table%2C%20warm%20golden%20rim%20light%2C%20premium%20minimal%20product%20photography%2C%20moody%20warm%20tones%2C%20clean%20dark%20background&width=800&height=1000&seq=nat-dif-vacuo-02&orientation=portrait',
  },
  {
    number: '03',
    kicker: 'História',
    title: 'Mais de duas décadas na mesma cozinha',
    desc: 'Crescemos ouvindo quem compra. A receita mudou pouco nesse tempo — o cuidado, nenhum pouco. É por isso que a Naturalle já faz parte da rotina de Fortaleza.',
    image:
      'https://readdy.ai/api/search-image?query=Chef%20hands%20carefully%20assembling%20artisanal%20sandwiches%20on%20a%20worn%20wooden%20board%20inside%20a%20warm%20dim%20kitchen%2C%20golden%20hour%20light%20through%20a%20window%2C%20documentary%20editorial%20food%20photography%2C%20cozy%20premium%20mood&width=800&height=1000&seq=nat-dif-historia-03&orientation=portrait',
  },
  {
    number: '04',
    kicker: 'Logística',
    title: 'Chega gelado, chega na hora',
    desc: 'Frota refrigerada e rota planejada ponto a ponto. Do centro ao interior, sua encomenda desembarca no prazo combinado — e sem taxa escondida no fim.',
    image:
      'https://readdy.ai/api/search-image?query=Stacked%20wooden%20crates%20filled%20with%20wrapped%20fresh%20food%20being%20loaded%20into%20a%20delivery%20van%2C%20dark%20warehouse%20background%2C%20warm%20amber%20lighting%2C%20editorial%20logistics%20photography%2C%20premium%20moody%20tones&width=800&height=1000&seq=nat-dif-logistica-04&orientation=portrait',
  },
  {
    number: '05',
    kicker: 'Parceria',
    title: 'Feito para quem revende',
    desc: 'Academias, conveniências, cantinas e lanchonetes: montamos condição personalizada, estoque previsível e suporte pra você vender sem se preocupar com a reposição.',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20deli%20counter%20display%20with%20neatly%20arranged%20fresh%20sandwiches%2C%20dark%20wood%20and%20warm%20spotlights%2C%20boutique%20food%20store%20interior%2C%20premium%20editorial%20photography%2C%20moody%20ambience&width=800&height=1000&seq=nat-dif-parceria-05&orientation=portrait',
  },
  {
    number: '06',
    kicker: 'Ocasiões',
    title: 'Do café da equipe ao casamento',
    desc: 'Kits para eventos no tamanho exato da sua ocasião. Reunião de time, coffee break, festa de família — a gente monta, embala e entrega pronto pra servir.',
    image:
      'https://readdy.ai/api/search-image?query=Elegant%20catering%20table%20set%20for%20a%20private%20event%20with%20finger%20food%20and%20fresh%20bites%2C%20warm%20candlelight%2C%20dark%20elegant%20venue%2C%20luxury%20editorial%20event%20photography%2C%20sophisticated%20moody%20tones&width=800&height=1000&seq=nat-dif-eventos-06&orientation=portrait',
  },
];

const FeaturesSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.querySelectorAll('.reveal').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const current = features[active];

  return (
    <section
      id="diferenciais"
      ref={sectionRef}
      className="relative bg-background-50 py-20 md:py-28 overflow-hidden"
    >
      {/* Soft warm glow accent */}
      <div className="pointer-events-none absolute -top-24 right-[-10%] h-[420px] w-[420px] rounded-full bg-gradient-to-br from-accent-500/10 via-secondary-500/5 to-transparent blur-2xl" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="mx-auto max-w-7xl">
          {/* Header — asymmetric */}
          <div className="mb-9 grid grid-cols-1 gap-5 md:mb-14 lg:mb-20 lg:grid-cols-[1.2fr_1fr] lg:items-end lg:gap-16">
            <div>
              <span className="reveal mb-4 inline-flex items-center gap-2 font-label text-xs font-medium uppercase tracking-[0.3em] text-secondary-500 md:text-sm">
                <span className="h-px w-8 bg-secondary-500/60" />
                Nossos Diferenciais
              </span>
              <h2 className="reveal reveal-delay-1 font-display text-3xl leading-[1.1] text-foreground-950 md:text-5xl lg:text-[58px]">
                Não vendemos só sanduíche.
                <br />
                Entregamos <span className="italic text-accent-500">cuidado</span>.
              </h2>
            </div>
            <p className="reveal reveal-delay-2 font-body text-sm leading-relaxed text-foreground-500 md:text-base lg:pb-3">
              Seis coisas que a gente faz questão de nunca abrir mão — da escolha do ingrediente
              até o momento em que você abre o pacote.
            </p>
          </div>

          {/* Interactive split */}
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
            {/* Left — visual panel (desktop only) */}
            <div className="reveal-zoom hidden lg:block lg:sticky lg:top-24 lg:self-start">
              <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[1.75rem] border border-background-200/60 sm:aspect-[16/11] lg:aspect-[4/5]">
                {features.map((feature, index) => (
                  <img
                    key={feature.number}
                    src={feature.image}
                    alt={`${feature.title} — Naturalle Sanduíches`}
                    className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 ease-out ${
                      index === active ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
                    }`}
                  />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/5" />

                {/* Kicker badge */}
                <div className="absolute left-6 top-6 flex items-center gap-2 rounded-full border border-secondary-500/30 bg-black/40 px-4 py-1.5 backdrop-blur-md">
                  <span className="h-1.5 w-1.5 rounded-full bg-secondary-500" />
                  <span className="font-label text-[11px] font-semibold uppercase tracking-[0.25em] text-secondary-300">
                    {current.kicker}
                  </span>
                </div>

                {/* Caption */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <span className="font-display text-5xl leading-none text-background-50/25 md:text-7xl">
                    {current.number}
                  </span>
                  <h3 className="mt-2 max-w-md font-display text-xl leading-snug text-background-50 md:text-2xl">
                    {current.title}
                  </h3>
                </div>
              </div>

              {/* Progress dots */}
              <div className="mt-5 flex items-center gap-3">
                <span className="font-label text-xs tabular-nums text-foreground-500">
                  {current.number}
                </span>
                <div className="flex flex-1 items-center gap-1.5">
                  {features.map((feature, index) => (
                    <button
                      key={feature.number}
                      type="button"
                      aria-label={`Ver ${feature.title}`}
                      onClick={() => setActive(index)}
                      className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                        index === active ? 'bg-accent-500' : 'bg-background-200'
                      }`}
                    />
                  ))}
                </div>
                <span className="font-label text-xs tabular-nums text-foreground-500">
                  06
                </span>
              </div>
            </div>

            {/* Right — interactive list */}
            <div className="reveal reveal-delay-2">
              {features.map((feature, index) => {
                const isActive = index === active;
                return (
                  <button
                    key={feature.number}
                    type="button"
                    onClick={() => setActive(index)}
                    onMouseEnter={() => setActive(index)}
                    className={`group w-full border-t border-background-200/60 py-6 text-left transition-colors duration-500 last:border-b md:py-7 ${
                      isActive ? 'border-secondary-500/40' : ''
                    }`}
                  >
                    <div className="flex items-start gap-5 md:gap-7">
                      <span
                        className={`mt-0.5 font-label text-xs tabular-nums transition-colors duration-500 md:text-sm ${
                          isActive ? 'text-accent-500' : 'text-foreground-400'
                        }`}
                      >
                        {feature.number}
                      </span>

                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-4">
                          <h3
                            className={`font-heading text-lg font-semibold leading-snug transition-colors duration-500 md:text-2xl ${
                              isActive
                                ? 'text-foreground-950'
                                : 'text-foreground-400 group-hover:text-foreground-800'
                            }`}
                          >
                            {feature.title}
                          </h3>
                          <span className="flex h-8 w-8 flex-none items-center justify-center">
                            <i
                              className={`text-xl transition-all duration-500 ${
                                isActive ? 'ri-subtract-line text-accent-500' : 'ri-add-line text-foreground-400'
                              }`}
                            />
                          </span>
                        </div>

                        <div
                          className={`overflow-hidden transition-all duration-500 ease-out ${
                            isActive ? 'mt-3 max-h-40 opacity-100' : 'mt-0 max-h-0 opacity-0'
                          }`}
                        >
                          <p className="max-w-xl font-body text-sm leading-relaxed text-foreground-400 md:text-[15px]">
                            {feature.desc}
                          </p>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;